import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, FileText, ArrowRight, Copy, RefreshCw,
         Lock, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import AdvancedTable from "@/components/table/AdvancedTable";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { apiErrorMessage } from "@/lib/apiError";
import { InlineText, InlinePicker, ApprovalDots,
         CommentsPopover, ActivityPopover } from "@/components/list-inline/InlineRow";
import LinkedBadges from "@/pages/app/gst-sales/LinkedBadges";
import LinkedPreviewDialog from "@/pages/app/gst-sales/LinkedPreviewDialog";
import DetailDialog from "@/pages/app/gst-sales/DetailDialog";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  submitted: "bg-sky-100 text-sky-700 border-sky-300",
  in_estimation: "bg-amber-100 text-amber-700 border-amber-300",
  quoted: "bg-emerald-100 text-emerald-700 border-emerald-300",
  cancelled: "bg-rose-100 text-rose-700 border-rose-300",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-300",
  pending_internal: "bg-sky-100 text-sky-700 border-sky-300",
  pending_client: "bg-indigo-100 text-indigo-700 border-indigo-300",
  revising: "bg-amber-100 text-amber-700 border-amber-300",
  rejected: "bg-rose-100 text-rose-700 border-rose-300",
};

const APPROVAL_DOT = {
  approved: "bg-emerald-500",
  rejected: "bg-rose-500",
  revision_requested: "bg-amber-500",
  pending: "bg-slate-300",
};
// eslint-disable-next-line no-unused-vars
const _dot_ref = APPROVAL_DOT;

export default function RFQList() {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("visible");
  const [sort, setSort] = useState("newest");
  const [priority, setPriority] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [members, setMembers] = useState([]);
  // Feb 2026 R293zze · Preload chain for every visible RFQ so the
  // Linked column shows real badges on first paint.
  const [chainById, setChainById] = useState({});
  // Feb 2026 R293zze · Linked column click-through — open hits in a
  // modal preview instead of a new tab for a tighter review loop.
  const [linkedPreview, setLinkedPreview] = useState(null); // { kind, id }
  const [gstDetailDoc, setGstDetailDoc] = useState(null);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const params = { scope, sort };
      if (filter !== "all") params.status = filter;
      if (q.trim()) params.q = q.trim();
      if (priority) params.priority = priority;
      if (clientFilter) params.client_id = clientFilter;
      if (brandFilter) params.brand_id = brandFilter;
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/rfqs`, { params });
      setRows(data || []);
      const ids = (data || []).map((r) => r.id).filter(Boolean);
      if (ids.length) {
        try {
          const cr = await api.post(
            `/gst/workspaces/${activeWorkspaceId}/rfqs/chain-bulk`,
            { ids });
          setChainById(cr.data || {});
        } catch { setChainById({}); }
      } else {
        setChainById({});
      }
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [activeWorkspaceId, filter, scope, sort, priority, clientFilter, brandFilter]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // Ref data for filters + inline edits
  useEffect(() => {
    if (!activeWorkspaceId) return;
    Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/clients`).then(r => r.data || []).catch(() => []),
      api.get(`/workspaces/${activeWorkspaceId}/brands`).then(r => r.data || []).catch(() => []),
      api.get(`/workspaces/${activeWorkspaceId}/members`).then(r => r.data || []).catch(() => []),
    ]).then(([cs, bs, ms]) => { setClients(cs); setBrands(bs); setMembers(ms); });
  }, [activeWorkspaceId]);

  const clientOpts = [{ value: "", label: "— No client —" },
    ...clients.map((c) => ({ value: c.id, label: c.name, hint: c.client_code }))];
  const brandOpts = (rowClientId) => [{ value: "", label: "— No brand —" },
    ...brands.filter((b) => !rowClientId || b.client_id === rowClientId)
             .map((b) => ({ value: b.id, label: b.name }))];
  const memberOpts = [{ value: "", label: "— No owner —" },
    ...members.map((m) => ({ value: m.user_id || m.id, label: m.name || m.email, hint: m.email }))];

  const duplicate = async (rid) => {
    try {
      const { data } = await api.post(`/rfqs/${rid}/duplicate`);
      toast.success(`Duplicated as ${data.number}`);
      load();
    } catch { toast.error("Failed to duplicate"); }
  };

  const patchRow = async (rid, patch) => {
    try {
      await api.patch(`/rfqs/${rid}`, patch);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    }
  };

  const nextSort = (key) => {
    // Cycle: newest <-> oldest, title asc, client asc, brand asc, priority, status, updated, due_asc/due_desc, revision
    if (key === "created" && sort === "newest") return "oldest";
    if (key === "created") return "newest";
    if (key === "due" && sort === "due_asc") return "due_desc";
    if (key === "due") return "due_asc";
    return key;
  };
  const sortIcon = (activeIfAny) => {
    if (!activeIfAny.includes(sort)) return <ArrowUpDown className="w-2.5 h-2.5 inline-block opacity-40" />;
    const arrow = sort === "oldest" || sort === "due_asc" ? <ArrowUp className="w-2.5 h-2.5 inline-block" /> : <ArrowDown className="w-2.5 h-2.5 inline-block" />;
    return arrow;
  };

  return (
    <div className="p-6 space-y-5" data-testid="rfq-list-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <FileText className="w-3 h-3" /> Estimates & Contracts
          </div>
          <h1 className="text-3xl font-bold mt-0.5 tracking-tight">Request for Quotation</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Capture requirements, build detailed estimates, convert to quotations, and track approvals.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5"
            data-testid="rfq-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1.5 font-medium"
            data-testid="rfq-new-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New RFQ
          </button>
        </div>
      </div>

      {/* Filters + Search + Sort + Scope */}
      <div className="space-y-2">
        {/* Iter 251 · Split filters onto their own row so the Client and
            Brand SearchableSelect popovers can't overlap when both are open
            or when the browser is narrow. */}
        <div className="flex items-center gap-2 flex-wrap">
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by title, number, requester name/email…"
                 className="text-xs px-3 py-1.5 border border-border bg-background focus:outline-none focus:border-primary min-w-[280px] flex-1 max-w-md"
                 data-testid="rfq-search" />
          <select value={scope} onChange={(e) => setScope(e.target.value)}
                  className="text-xs px-2 py-1.5 border border-border bg-background"
                  data-testid="rfq-scope"
                  title="RFQ visibility scope">
            <option value="visible">All visible</option>
            <option value="mine">Only mine</option>
            <option value="project">My projects</option>
          </select>
          <select value={priority} onChange={(e) => setPriority(e.target.value)}
                  className="text-xs px-2 py-1.5 border border-border bg-background"
                  data-testid="rfq-priority-filter">
            <option value="">Any priority</option>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
          {(clientFilter || brandFilter || priority || q) && (
            <button type="button" onClick={() => { setClientFilter(""); setBrandFilter(""); setPriority(""); setQ(""); }}
                     className="text-[10px] px-2 py-1.5 border border-border hover:bg-muted text-muted-foreground"
                     data-testid="rfq-clear-filters">
              Clear filters
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-56 shrink-0">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Client filter</label>
            <SearchableSelect
              value={clientFilter}
              onValueChange={setClientFilter}
              options={[{ value: "", label: "All clients" },
                        ...clients.map((c) => ({ value: c.id, label: c.name, hint: c.client_code }))]}
              placeholder="Client…"
              testid="rfq-filter-client"
              width="100%"
            />
          </div>
          <div className="w-56 shrink-0">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">Brand filter</label>
            <SearchableSelect
              value={brandFilter}
              onValueChange={setBrandFilter}
              options={[{ value: "", label: "All brands" },
                        ...brands.filter((b) => !clientFilter || b.client_id === clientFilter)
                                 .map((b) => ({ value: b.id, label: b.name }))]}
              placeholder="Brand…"
              testid="rfq-filter-brand"
              width="100%"
            />
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {["all", "draft", "pending_internal", "pending_client", "approved", "revising", "rejected", "cancelled"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1.5 border transition ${filter === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
              data-testid={`rfq-filter-${s}`}
            >
              {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>

      {/* Iter 245 · Advanced table (show/hide cols, filters, sort, resize, reorder, exports, saved layouts via tableKey) */}
      <AdvancedTable
        tableKey="rfqs"
        title="RFQs"
        rows={rows}
        loading={loading}
        emptyTitle="No RFQs match"
        emptyDescription="Adjust filters or click New RFQ to create one."
        onRowClick={(r) => nav(`/app/rfqs/${r.id}`)}
        columns={rfqColumns({ clientOpts, memberOpts, brandOpts, patchRow, duplicate, ws: activeWorkspaceId, nav, chainById, setLinkedPreview, setGstDetailDoc })}
      />

      {showNew && <NewRFQDialog wsId={activeWorkspaceId} onClose={() => setShowNew(false)} onCreated={(r) => { setShowNew(false); nav(`/app/rfqs/${r.id}`); }} />}

      <LinkedPreviewDialog
        open={!!linkedPreview}
        ws={activeWorkspaceId}
        kind={linkedPreview?.kind}
        docId={linkedPreview?.id}
        onClose={() => setLinkedPreview(null)}
      />
      <DetailDialog
        doc={gstDetailDoc}
        ws={activeWorkspaceId}
        onClose={() => setGstDetailDoc(null)}
      />
    </div>
  );
}

function rfqColumns({ clientOpts, memberOpts, brandOpts, patchRow, duplicate, ws, nav, chainById = {}, setLinkedPreview, setGstDetailDoc }) {
  return [
    { key: "number", label: "Number", width: 110, sortable: true, filterType: "text", frozen: true,
      render: (r) => (
        <Link to={`/app/rfqs/${r.id}`} className="font-mono text-[11px] text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}>{r.number}</Link>
      ) },
    { key: "title", label: "Title", width: 260, sortable: true, filterType: "text",
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <InlineText value={r.title || ""} onSave={(v) => patchRow(r.id, { title: v })}
                       locked={r.is_locked} testid={`rfq-title-${r.id}`} />
        </div>
      ) },
    { key: "client_name", label: "Client", width: 160, sortable: true, filterType: "text",
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <InlinePicker value={r.client_id || ""}
                        display={r.client_name || <span className="opacity-50">—</span>}
                        options={clientOpts}
                        onSave={(v) => patchRow(r.id, { client_id: v || null, brand_id: null })}
                        locked={r.is_locked} testid={`rfq-client-${r.id}`} />
        </div>
      ) },
    { key: "brand_name", label: "Brand", width: 140, sortable: true, filterType: "text",
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <InlinePicker value={r.brand_id || ""}
                        display={r.brand_name || <span className="opacity-50">—</span>}
                        options={brandOpts(r.client_id)}
                        onSave={(v) => patchRow(r.id, { brand_id: v || null })}
                        locked={r.is_locked} testid={`rfq-brand-${r.id}`} />
        </div>
      ) },
    { key: "owner_name", label: "Owner", width: 130, sortable: true, filterType: "text",
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <InlinePicker value={r.owner_id || ""}
                        display={r.owner_name || <span className="opacity-50">—</span>}
                        options={memberOpts}
                        onSave={(v) => patchRow(r.id, { owner_id: v || null })}
                        locked={r.is_locked} testid={`rfq-owner-${r.id}`} />
        </div>
      ) },
    { key: "requested_by_name", label: "Requestor", width: 130, sortable: true, filterType: "text" },
    { key: "line_count", label: "Lines", width: 70, sortable: true, align: "right", type: "number" },
    { key: "priority", label: "Priority", width: 90, sortable: true, filterType: "select",
      filterOptions: ["low","normal","high","urgent"].map((v) => ({ value: v, label: v })),
      render: (r) => (
        <span onClick={(e) => e.stopPropagation()}>
          {r.is_locked ? <span className="capitalize text-[11px]">{r.priority}</span> : (
            <select value={r.priority || "normal"} onChange={(e) => patchRow(r.id, { priority: e.target.value })}
                    className="text-[11px] px-1 py-0.5 bg-transparent border border-transparent hover:border-border focus:border-primary focus:outline-none capitalize">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          )}
        </span>
      ) },
    { key: "status", label: "Status", width: 130, sortable: true, filterType: "select",
      filterOptions: ["draft","pending_internal","pending_client","approved","revising","rejected","cancelled"].map((v) => ({ value: v, label: v.replace(/_/g," ") })),
      render: (r) => (
        <span className={`inline-block px-2 py-0.5 border text-[10px] whitespace-nowrap ${STATUS_STYLES[r.status] || STATUS_STYLES.draft}`}>
          {r.is_locked && <Lock className="w-2.5 h-2.5 inline-block mr-0.5 -mt-px" />}
          {r.status?.replace(/_/g, " ")}
        </span>
      ) },
    { key: "approvals", label: "Approvals", width: 100,
      render: (r) => <ApprovalDots internal={r.internal_approval_status} client={r.client_approval_status} /> },
    { key: "revision_no", label: "Rev", width: 60, sortable: true, align: "center", type: "number",
      render: (r) => <span className="font-mono text-[11px]">v{r.revision_no || 1}</span> },
    { key: "requested_delivery", label: "Due", width: 110, sortable: true, filterType: "date-range" },
    { key: "created_at", label: "Created", width: 110, sortable: true, filterType: "date-range",
      render: (r) => <span className="text-[11px] text-muted-foreground">{fmtRelative(r.created_at)}</span> },
    { key: "updated_at", label: "Updated", width: 110, sortable: true, filterType: "date-range",
      render: (r) => <span className="text-[11px] text-muted-foreground">{fmtRelative(r.updated_at)}</span> },
    { key: "linked", label: "Linked", width: 160,
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <LinkedBadges
            ws={ws}
            docId={r.id}
            source="rfq"
            preloaded={chainById[r.id] || null}
            onOpen={async (hit) => {
              if (hit.kind === "rfqs") {
                setLinkedPreview?.({ kind: "rfq", id: hit.id });
                return;
              }
              if (hit.kind === "estimates") {
                setLinkedPreview?.({ kind: "estimate", id: hit.id });
                return;
              }
              // GST doc kind — fetch and open DetailDialog in-place
              try {
                const d = await api.get(`/gst/workspaces/${ws}/documents/${hit.id}`);
                setGstDetailDoc?.(d.data);
              } catch {
                const typeToTab = { quotations: "estimate", sales_orders: "sales_order", invoices: "invoice", credit_notes: "credit_note" };
                const tab = typeToTab[hit.kind] || "invoice";
                nav(`/app/sales?t=${tab}&open=${hit.id}`);
              }
            }}
            compact
          />
        </div>
      ) },
    { key: "actions", label: "", width: 130,
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-0.5 justify-end">
          <CommentsPopover docType="rfq" docId={r.id} count={r.comments_count} />
          <ActivityPopover docType="rfq" docId={r.id} extras={[`/rfqs/${r.id}/approval-history`]} />
          <button type="button" onClick={() => duplicate(r.id)}
                   className="text-[10px] px-1 py-1 text-muted-foreground hover:text-foreground"
                   title="Duplicate">
            <Copy className="w-3 h-3" />
          </button>
        </div>
      ) },
  ];
}

// ────────────────────── Iter 244 · Row with inline edit ──────────────────────
function NewRFQDialog({ wsId, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [clientId, setClientId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/clients`).then(({ data }) => setClients(data || []));
    api.get(`/workspaces/${wsId}/brands`).then(({ data }) => setBrands(data || []));
  }, [wsId]);

  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/rfqs`, {
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        client_id: clientId || undefined,
        brand_id: brandId || undefined,
      });
      toast.success(`Created ${data.number}`);
      onCreated(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="rfq-new-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-background border border-border shadow-2xl">
        <div className="px-4 py-3 border-b border-border bg-muted/40 font-semibold text-sm">New RFQ</div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full text-sm px-2 py-1.5 mt-0.5 bg-background border border-border focus:outline-none focus:border-primary" data-testid="rfq-new-title" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border focus:outline-none focus:border-primary" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Client</label>
              <SearchableSelect
                value={clientId}
                onValueChange={(v) => { setClientId(v); setBrandId(""); }}
                options={[{ value: "", label: "— No client —" },
                          ...clients.map((c) => ({ value: c.id, label: c.name, hint: c.client_code }))]}
                placeholder="Search client…"
                testid="rfq-new-client"
                contentClassName="w-[min(360px,calc(100vw-3rem))]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Brand</label>
              <SearchableSelect
                value={brandId}
                onValueChange={setBrandId}
                options={[{ value: "", label: "— No brand —" },
                          ...brands.filter((b) => !clientId || b.client_id === clientId)
                                  .map((b) => ({ value: b.id, label: b.name }))]}
                placeholder={clientId ? "Search brand for this client…" : "Search brand…"}
                testid="rfq-new-brand"
                contentClassName="w-[min(360px,calc(100vw-3rem))]"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Priority</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full sm:w-56 text-xs px-2 py-1.5 mt-0.5 bg-background border border-border" data-testid="rfq-new-priority">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium" data-testid="rfq-new-save">
            {saving ? "Creating…" : "Create RFQ"}
          </button>
        </div>
      </div>
    </div>
  );
}
